import {
  Box,
  Card,
  Container,
  Grid,
  Button,
  Typography,
  CardContent,
  CardHeader,
  Tooltip,
  IconButton,
  Divider,
  Chip,
  Avatar,
  Stack,
} from '@mui/material';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { imgUrl, url } from 'src/Helpers/utils/Constant';
import GlobalLoader from 'src/components/SuspenseLoader/GlobalLoader';
import moment from 'moment';
import { useReactToPrint } from 'react-to-print';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import PdfAndImageView from 'src/components/pdfAndImageView/PdfAndImageView';
import { ISingleTraineeDraftApplication } from '../types/traineeDraftApplicationTypes';
import DraftShortCourseForm from '../components/DraftShortCourseForm';
import InfoRow from '../components/InfoRow';

const SingleTraineeDraftApplication = () => {
  const { draftId } = useParams();
  const [singleDraft, setSingleDraft] =
    useState<ISingleTraineeDraftApplication>({} as ISingleTraineeDraftApplication);
  const [loading, setLoading] = useState<boolean>(true);
  const [imageViewOpen, setImageViewOpen] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<string | null>('');

  const componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `draft_course_application_${draftId}`,
    removeAfterPrint: true,
  });

  const handleOpenFile = (file: string | null | undefined) => {
    if (file) {
      setImageFile(file);
      setImageViewOpen(true);
    }
  };

  useEffect(() => {
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    setLoading(true);

    axios
      .get(`${url}/api/training/course/application/draft/list/${draftId}`, {
        cancelToken: source.token,
      })
      .then((res) => {
        if (res.data?.success && res.data?.data) {
          setSingleDraft(res.data.data);
        }
        setLoading(false);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          console.error('Error fetching draft application:', err);
        }
        setLoading(false);
      });

    return () => {
      source.cancel();
    };
  }, [draftId]);

  return (
    <>
      <Helmet>
        <title>Atab - Single Draft Application Information</title>
      </Helmet>

      {loading ? (
        <GlobalLoader />
      ) : (
        <Container maxWidth='lg' sx={{ py: 3 }}>
          {/* Header Action Bar */}
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
            mb={2}
          >
            <Box display='flex' alignItems='center' gap={1.5}>
              <Link to='/training-trainee/draft-applications' style={{ textDecoration: 'none' }}>
                <Button
                  variant='outlined'
                  size='small'
                  startIcon={<ArrowBackIcon />}
                >
                  Back to Drafts
                </Button>
              </Link>
              <Chip
                label='Draft Application'
                color='warning'
                variant='outlined'
                size='small'
              />
            </Box>
            <Box display='flex' alignItems='center' gap={1}>
              <Tooltip title='Print Application' arrow>
                <IconButton
                  color='primary'
                  onClick={handlePrint}
                  sx={{
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                  }}
                >
                  <PrintIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {singleDraft?.training_course_application_draft_id ? (
            <Stack spacing={3}>
              {/* Profile & Main Info Card */}
              <Card>
                <CardHeader
                  title={
                    <Box display='flex' alignItems='center' gap={1.5}>
                      <Typography variant='h4'>
                        {singleDraft.training_course_application_name || 'Applicant Information'}
                      </Typography>

                    </Box>
                  }
                  subheader={
                    <Typography variant='subtitle1' color='text.secondary' mt={0.5}>
                      Course: <strong>{singleDraft.atab_training_course_name || 'N/A'}</strong>
                    </Typography>
                  }
                />
                <Divider />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4} md={3} textAlign='center'>
                      <Avatar
                        src={
                          singleDraft.training_course_application_photo
                            ? `${imgUrl}/${singleDraft.training_course_application_photo}`
                            : '/static/images/avatars/avatar2.png'
                        }
                        alt={singleDraft.training_course_application_name}
                        sx={{
                          width: 140,
                          height: 140,
                          margin: '0 auto 16px',
                          border: '3px solid #e0e0e0',
                        }}
                      />
                      {singleDraft.training_course_application_trainee_id && (
                        <Box mb={1}>
                          <Link
                            to={`/training-trainee/details/${singleDraft.training_course_application_trainee_id}`}
                            style={{ textDecoration: 'none' }}
                          >
                            <Button size='small' variant='outlined'>
                              View Trainee Account
                            </Button>
                          </Link>
                          <Typography variant='caption' display='block' color='text.secondary' mt={0.5}>
                            Trainee ID: #{singleDraft.training_course_application_trainee_id}
                          </Typography>
                        </Box>
                      )}
                      {singleDraft.user_training_trainee_name && (
                        <Typography variant='caption' display='block' color='text.secondary'>
                          Username: {singleDraft.user_training_trainee_name}
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} sm={8} md={9}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <InfoRow
                            label='Full Name'
                            value={singleDraft.training_course_application_name}
                            labelWidth={130}
                          />
                          <InfoRow
                            label='Email Address'
                            value={singleDraft.training_course_application_email}
                            labelWidth={130}
                          />
                          <InfoRow
                            label='Mobile Number'
                            value={singleDraft.training_course_application_mobile}
                            labelWidth={130}
                          />
                          <InfoRow
                            label='WhatsApp Number'
                            value={singleDraft.training_course_application_whatsapp_number}
                            labelWidth={130}
                          />
                          <InfoRow
                            label='Gender'
                            value={
                              singleDraft.training_course_application_gender ? (
                                <span style={{ textTransform: 'capitalize' }}>
                                  {singleDraft.training_course_application_gender}
                                </span>
                              ) : null
                            }
                            labelWidth={130}
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <InfoRow
                            label='Date of Birth'
                            value={
                              singleDraft.training_course_application_birthdate
                                ? `${moment(singleDraft.training_course_application_birthdate).format('DD-MMM-YYYY')} (${moment(singleDraft.training_course_application_birthdate).fromNow(true)} old)`
                                : null
                            }
                            labelWidth={130}
                          />
                          <InfoRow
                            label='Blood Group'
                            value={singleDraft.training_course_application_blood_group}
                            labelWidth={130}
                          />
                          <InfoRow
                            label='Marital Status'
                            value={singleDraft.training_course_application_maritial_status}
                            labelWidth={130}
                          />
                          <InfoRow
                            label='Religion'
                            value={singleDraft.training_course_application_religion}
                            labelWidth={130}
                          />
                          <InfoRow
                            label='Nationality'
                            value={singleDraft.training_course_application_nationality}
                            labelWidth={130}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Educational Qualifications */}
              <Card>
                <CardHeader title='Educational Background' />
                <Divider />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <InfoRow
                        label='Last Qualification'
                        value={singleDraft.training_course_application_last_academic_qualification}
                        labelWidth={150}
                      />
                      <InfoRow
                        label='Degree / Exam'
                        value={singleDraft.training_course_application_degree}
                        labelWidth={150}
                      />
                      <InfoRow
                        label='Institute'
                        value={singleDraft.training_course_application_institute}
                        labelWidth={150}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InfoRow
                        label='Board / University'
                        value={singleDraft.training_course_application_board}
                        labelWidth={150}
                      />
                      <InfoRow
                        label='Passing Year'
                        value={singleDraft.training_course_application_passing_year}
                        labelWidth={150}
                      />
                      <InfoRow
                        label='CGPA / Grade'
                        value={singleDraft.training_course_application_cgpa}
                        labelWidth={150}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Address & Location */}
              <Card>
                <CardHeader title='Address & Location Details' />
                <Divider />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <InfoRow
                        label='Present Address'
                        value={singleDraft.training_course_application_present_address}
                        labelWidth={150}
                      />
                      <InfoRow
                        label='Division'
                        value={singleDraft.division_name}
                        labelWidth={150}
                      />
                      <InfoRow
                        label='District'
                        value={singleDraft.district_name}
                        labelWidth={150}
                      />
                      <InfoRow
                        label='Area'
                        value={singleDraft.area_name}
                        labelWidth={150}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InfoRow
                        label='Permanent Address'
                        value={singleDraft.training_course_application_parmament_address}
                        labelWidth={150}
                      />
                      <InfoRow
                        label='Thana / Upazila'
                        value={singleDraft.thana_name}
                        labelWidth={150}
                      />
                      <InfoRow
                        label='Post Code'
                        value={singleDraft.training_course_application_post_code}
                        labelWidth={150}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Guardian & Family Information */}
              <Card>
                <CardHeader title='Guardian & Family Information' />
                <Divider />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <InfoRow
                        label="Father's Name"
                        value={singleDraft.training_course_application_fathers_name}
                        labelWidth={150}
                      />
                      <InfoRow
                        label="Father's Occupation"
                        value={singleDraft.training_course_application_father_occupation}
                        labelWidth={150}
                      />
                      <InfoRow
                        label="Mother's Name"
                        value={singleDraft.training_course_application_mothers_name}
                        labelWidth={150}
                      />
                      <InfoRow
                        label="Mother's Occupation"
                        value={singleDraft.training_course_application_mother_occupation}
                        labelWidth={150}
                      />
                      <InfoRow
                        label="Parents' Income"
                        value={
                          singleDraft.training_course_application_parents_income
                            ? `৳ ${singleDraft.training_course_application_parents_income}`
                            : null
                        }
                        labelWidth={150}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InfoRow
                        label='Guardian Name'
                        value={
                          singleDraft.training_course_application_guardian_name
                            ? `${singleDraft.training_course_application_guardian_name}${singleDraft.training_course_application_guardian_relation ? ' (' + singleDraft.training_course_application_guardian_relation + ')' : ''}`
                            : null
                        }
                        labelWidth={150}
                      />
                      <InfoRow
                        label='Guardian Phone'
                        value={singleDraft.training_course_application_guardian_phone}
                        labelWidth={150}
                      />
                      <InfoRow
                        label='Guardian Address'
                        value={singleDraft.training_course_application_guardian_address}
                        labelWidth={150}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Member / Company & Additional Info */}
              <Card>
                <CardHeader title='Company & Application Details' />
                <Divider />
                <CardContent>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <InfoRow
                        label='Company Name'
                        value={singleDraft.user_member_company_name}
                        labelWidth={150}
                      />
                      <InfoRow
                        label='Member ID'
                        value={singleDraft.training_course_application_member_id}
                        labelWidth={150}
                      />
                      <InfoRow
                        label='Source of Info'
                        value={singleDraft.training_course_application_info_source}
                        labelWidth={150}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <InfoRow
                        label='NID Number'
                        value={singleDraft.training_course_application_nid_number}
                        labelWidth={150}
                      />
                      <InfoRow
                        label='Created Date'
                        value={
                          singleDraft.training_course_application_created_at
                            ? moment(singleDraft.training_course_application_created_at).format('DD-MMM-YYYY, hh:mm A')
                            : null
                        }
                        labelWidth={150}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Attached Documents */}
              <Card>
                <CardHeader title='Attached Documents & Files' />
                <Divider />
                <CardContent>
                  <Grid container spacing={2}>
                    {/* Photo */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Box
                        p={2}
                        border='1px solid #e0e0e0'
                        borderRadius={1}
                        display='flex'
                        justifyContent='space-between'
                        alignItems='center'
                      >
                        <Box display='flex' alignItems='center' gap={1}>
                          <FilePresentIcon color='primary' />
                          <Typography variant='body2' fontWeight='bold'>
                            Photo
                          </Typography>
                        </Box>
                        {singleDraft.training_course_application_photo ? (
                          <Button
                            size='small'
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleOpenFile(singleDraft.training_course_application_photo)}
                          >
                            View
                          </Button>
                        ) : (
                          <Typography variant='caption' color='text.secondary'>
                            Not Provided
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* Signature */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Box
                        p={2}
                        border='1px solid #e0e0e0'
                        borderRadius={1}
                        display='flex'
                        justifyContent='space-between'
                        alignItems='center'
                      >
                        <Box display='flex' alignItems='center' gap={1}>
                          <FilePresentIcon color='primary' />
                          <Typography variant='body2' fontWeight='bold'>
                            Signature
                          </Typography>
                        </Box>
                        {singleDraft.training_course_application_signature_file ? (
                          <Button
                            size='small'
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleOpenFile(singleDraft.training_course_application_signature_file)}
                          >
                            View
                          </Button>
                        ) : (
                          <Typography variant='caption' color='text.secondary'>
                            Not Provided
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* NID Front */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Box
                        p={2}
                        border='1px solid #e0e0e0'
                        borderRadius={1}
                        display='flex'
                        justifyContent='space-between'
                        alignItems='center'
                      >
                        <Box display='flex' alignItems='center' gap={1}>
                          <FilePresentIcon color='primary' />
                          <Typography variant='body2' fontWeight='bold'>
                            NID Front
                          </Typography>
                        </Box>
                        {singleDraft.training_course_application_nid_front_file ? (
                          <Button
                            size='small'
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleOpenFile(singleDraft.training_course_application_nid_front_file)}
                          >
                            View
                          </Button>
                        ) : (
                          <Typography variant='caption' color='text.secondary'>
                            Not Provided
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* NID Back */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Box
                        p={2}
                        border='1px solid #e0e0e0'
                        borderRadius={1}
                        display='flex'
                        justifyContent='space-between'
                        alignItems='center'
                      >
                        <Box display='flex' alignItems='center' gap={1}>
                          <FilePresentIcon color='primary' />
                          <Typography variant='body2' fontWeight='bold'>
                            NID Back
                          </Typography>
                        </Box>
                        {singleDraft.training_course_application_nid_back_file ? (
                          <Button
                            size='small'
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleOpenFile(singleDraft.training_course_application_nid_back_file)}
                          >
                            View
                          </Button>
                        ) : (
                          <Typography variant='caption' color='text.secondary'>
                            Not Provided
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* CV File */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Box
                        p={2}
                        border='1px solid #e0e0e0'
                        borderRadius={1}
                        display='flex'
                        justifyContent='space-between'
                        alignItems='center'
                      >
                        <Box display='flex' alignItems='center' gap={1}>
                          <FilePresentIcon color='primary' />
                          <Typography variant='body2' fontWeight='bold'>
                            CV / Resume
                          </Typography>
                        </Box>
                        {singleDraft.training_course_application_cv_file ? (
                          <Button
                            size='small'
                            startIcon={<VisibilityIcon />}
                            onClick={() => handleOpenFile(singleDraft.training_course_application_cv_file)}
                          >
                            View
                          </Button>
                        ) : (
                          <Typography variant='caption' color='text.secondary'>
                            Not Provided
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* Academic Certificate */}
                    <Grid item xs={12} sm={6} md={4}>
                      <Box
                        p={2}
                        border='1px solid #e0e0e0'
                        borderRadius={1}
                        display='flex'
                        justifyContent='space-between'
                        alignItems='center'
                      >
                        <Box display='flex' alignItems='center' gap={1}>
                          <FilePresentIcon color='primary' />
                          <Typography variant='body2' fontWeight='bold'>
                            Academic Certificate
                          </Typography>
                        </Box>
                        {singleDraft.training_course_application_last_academic_certificate_file ? (
                          <Button
                            size='small'
                            startIcon={<VisibilityIcon />}
                            onClick={() =>
                              handleOpenFile(
                                singleDraft.training_course_application_last_academic_certificate_file
                              )
                            }
                          >
                            View
                          </Button>
                        ) : (
                          <Typography variant='caption' color='text.secondary'>
                            Not Provided
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* Member Document */}
                    {singleDraft.training_course_application_member_document && (
                      <Grid item xs={12} sm={6} md={4}>
                        <Box
                          p={2}
                          border='1px solid #e0e0e0'
                          borderRadius={1}
                          display='flex'
                          justifyContent='space-between'
                          alignItems='center'
                        >
                          <Box display='flex' alignItems='center' gap={1}>
                            <FilePresentIcon color='primary' />
                            <Typography variant='body2' fontWeight='bold'>
                              Member Document
                            </Typography>
                          </Box>
                          <Button
                            size='small'
                            startIcon={<VisibilityIcon />}
                            onClick={() =>
                              handleOpenFile(
                                singleDraft.training_course_application_member_document
                              )
                            }
                          >
                            View
                          </Button>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
            </Stack>
          ) : (
            <Card sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant='h5' color='text.secondary' gutterBottom>
                Draft Application Not Found
              </Typography>
              <Typography variant='body2' color='text.secondary' mb={2}>
                The requested draft application #{draftId} could not be loaded.
              </Typography>
              <Link to='/training-trainee/draft-applications' style={{ textDecoration: 'none' }}>
                <Button variant='contained'>Back to Draft List</Button>
              </Link>
            </Card>
          )}

          {/* Document Preview Modal */}
          {imageViewOpen && (
            <PdfAndImageView
              open={imageViewOpen}
              setOpen={setImageViewOpen}
              file={imageFile}
            />
          )}

          {/* Hidden Print Container */}
          <div style={{ display: 'none' }}>
            <DraftShortCourseForm
              componentRef={componentRef}
              singleTraineeDraftApplication={singleDraft}
            />
          </div>
        </Container>
      )}
    </>
  );
};

export default SingleTraineeDraftApplication;
